/**
 * `event.v1` — wire → canonical decoder for Event-shaped cells.
 *
 * Decodes the cell through `SheetsNetwork.EventCell.MODEL` with
 * `WireDecode.partial` (every declared field that decodes is hydrated —
 * `DateTime<Utc>`, `NaiveDate`, enums — the rest left out; the SDK's own
 * optimistic cells carry no `etag`/`source`) and applies the rename layer that separates the short wire names from the
 * canonical `dtype.Event` field names.
 *
 * Rename table:
 *   - `t`  → `title`   (empty-string fallback when absent)
 *   - `st` → `start`   (synthesized from `startDate` if null)
 *   - `et` → `end`     (synthesized from `endDate` if null)
 *
 * Full-day synthesis: when `st`/`et` are absent but
 * `startDate`/`endDate` are present, the decoder synthesizes
 * midnight-UTC bounds so downstream indices can treat
 * `start` / `end` uniformly.
 *
 * Tolerant (`WireDecode.partial`), not strict: the SDK's own optimistic
 * event cells carry no `etag`/`source` and older stored cells may miss
 * newer fields, so the decoder checks only the fields an event needs. The
 * one enum, `visibility`, decodes through the model's `stringUnion` (an
 * unknown value is left out).
 *
 * Null return: missing `id`, or missing both temporal forms,
 * drops the cell. The `NormalizingBackend` adapter filters
 * nulls with a diagnostics counter increment.
 */

import { AuthId, CalendarId, EventId } from "../ids/ids.ts";
import type { Option } from "../core/option.ts";
import { DateTime, type NaiveDate, Utc } from "@tento-chrono";

import type { Attendee, CalendarEvent, CellValue } from "../core/dtype.ts";
import type { EncodingDecoder } from "../core/encoding-registry.ts";
import { Json, jsonObjectOf } from "../core/json.ts";
import { SheetsNetwork } from "../wire/rows-network.ts";
import { WireDecode } from "../wire/wire-decode.ts";

// Facts are minted only for temporal values parsed by this module's wire decoder.
const wireEventDays = new WeakMap<object, number>();

/** Internal day fact; public rename inputs and proxy wrappers are never registered. */
export function wireEventDay(endpoint: object): Option<number> {
  return wireEventDays.get(endpoint) ?? null;
}

/** The `event.v1` wire-encoding name a calendar source row declares in its metadata. */
export const EVENT_V1 = "event.v1";

/**
 * Rename + full-day synthesis on a decoded `SheetsNetwork.EventCell` (whole,
 * or the fields `WireDecode.partial` kept). Exported so callers that already
 * hold one (e.g. lona-js's `EventsIndex` cache) reuse the rename.
 *
 * Drops events missing `id`, `authId`, `calendarId`, or both time
 * forms — the decoder is the designated failure point per
 * `feedback_no_nullish_for_required_fields`.
 */
export function eventDbToEvent(parsed: Partial<SheetsNetwork.EventCell>): Option<CalendarEvent> {
  const id = typeof parsed.id === "string" ? EventId.parse(parsed.id) : null;
  if (id == null) return null;
  // Provider identity is load-bearing downstream (calendar-visibility
  // filter, event-details UI). A wire shape missing either field is
  // broken — drop it rather than painting empty-string defaults.
  const authId = typeof parsed.authId === "string" ? AuthId.parse(parsed.authId) : null;
  if (authId == null) return null;
  const calendarId = typeof parsed.calendarId === "string"
    ? CalendarId.parse(parsed.calendarId === "primary" ? authId : parsed.calendarId)
    : null;
  if (calendarId == null) return null;
  const recurringEventId = typeof parsed.recurringEventId === "string"
    ? EventId.parse(parsed.recurringEventId)
    : null;

  const start = parsed.st ?? fullDayStart(parsed.startDate ?? null);
  const end = parsed.et ?? fullDayEnd(parsed.endDate ?? null);
  if (!start || !end) return null;
  const attendees = attendeesOf(parsed.attendees ?? null);

  return {
    // camelCase passthroughs ride along via spread — Rust-side
    // already emits `recurrence`, etc. A `null` wire field is left out of
    // the spread; an extra the wire left out or sent `null` is set `null`
    // below. Those keys come after the wire's own, so `CalendarEvent.wire`
    // (which leaves out a `null` extra) gives back the cell's keys in the
    // cell's order. (`recurringEventId` rides only branded, `attendees`
    // only decoded, below.)
    ...withoutNulls({ ...parsed, recurringEventId: null, attendees: null }),
    id,
    title: parsed.t ?? "",
    authId,
    // "primary" is the server's row-key sentinel for the account's primary
    // calendar (the calendar list stores it under its concrete id == authId).
    // Canonicalize to the concrete id above — the exact inverse of
    // `EventsTable._calendarRowKey`'s outbound email→"primary" mapping — so the
    // canonical Event model never carries the sentinel.
    calendarId,
    recurringEventId,
    start,
    end,
    attendees,
    creatorIsSelf: parsed.creatorIsSelf ?? null,
    eventType: parsed.eventType ?? null,
    startDate: parsed.startDate ?? null,
    endDate: parsed.endDate ?? null,
    recurrence: parsed.recurrence ?? null,
    timezone: parsed.timezone ?? null,
    startTimeZone: parsed.startTimeZone ?? null,
    endTimeZone: parsed.endTimeZone ?? null,
    suggestedTimezone: parsed.suggestedTimezone ?? null,
    category: parsed.category ?? null,
    suggestedCategory: parsed.suggestedCategory ?? null,
    useSuggestedCategory: parsed.useSuggestedCategory ?? null,
    description: parsed.description ?? null,
    location: parsed.location ?? null,
    conferenceData: parsed.conferenceData ?? null,
  } satisfies CalendarEvent;
}

/** `value` without its `null`-valued fields. */
function withoutNulls<T extends object>(value: T): { [K in keyof T]?: NonNullable<T[K]> } {
  const out: { [K in keyof T]?: NonNullable<T[K]> } = {};
  for (const key of Object.keys(value) as (keyof T)[]) {
    const field = value[key];
    if (field != null) out[key] = field;
  }
  return out;
}

/**
 * The wire's `attendees` (untyped JSON) as `Attendee`s: every entry that is
 * an object with a string `email`, its optional fields kept when they have
 * the right type. `null` when the wire carries no attendee list.
 */
function attendeesOf(wire: Option<Json>): Option<readonly Attendee[]> {
  if (!Array.isArray(wire)) return null;
  const out: Attendee[] = [];
  for (const item of wire) {
    const obj = Json.object(item);
    const email = Json.string(obj, "email");
    if (email == null) continue;
    out.push({
      email,
      displayName: Json.string(obj, "displayName"),
      responseStatus: Json.string(obj, "responseStatus"),
      self: Json.boolean(obj, "self"),
    });
  }
  return out;
}

/** Decodes an `event.v1` wire cell to a canonical event (`null`: not an event). */
export const eventV1: EncodingDecoder = (wire): Option<Json | CellValue> => {
  // The JSON object the wire sent; anything else — a scalar, an array — is
  // not an event.
  const json = jsonObjectOf(wire);
  if (json == null) return null;
  // The keys the cell model does not declare ride along unvalidated, as they
  // always have (the rename spreads them onto the canonical event).
  const decoded = WireDecode.partial(SheetsNetwork.EventCell.MODEL, json);
  const event = eventDbToEvent({ ...decoded.undeclared, ...decoded.value });
  if (event == null) return null;
  const fields = decoded.value;
  if (fields.st != null) wireEventDays.set(fields.st, fields.st.dse);
  if (fields.et != null) wireEventDays.set(fields.et, fields.et.dse);
  if (fields.startDate != null) wireEventDays.set(fields.startDate, fields.startDate.dse);
  if (fields.endDate != null) wireEventDays.set(fields.endDate, fields.endDate.dse);
  return event;
};

/**
 * The epoch days an event value spans, read once from the value a cell store
 * has just copied (the store's certifier for event.v1 cells): its dates for an
 * all-day event, else the days of its start and end instants; `null` for a
 * value that does not carry them. Pure: it registers nothing.
 */
export function eventV1Days(value: unknown): Option<{ readonly start: number; readonly end: number }> {
  if (typeof value !== "object" || value === null) return null;
  const all = "startDate" in value && "endDate" in value && value.startDate != null && value.endDate != null;
  const start = dayOf(all ? ("startDate" in value ? value.startDate : null) : ("start" in value ? value.start : null));
  const end = dayOf(all ? ("endDate" in value ? value.endDate : null) : ("end" in value ? value.end : null));
  return start == null || end == null ? null : { start, end };
}

/** A chrono value's epoch day (`dse`), or `null`. */
function dayOf(endpoint: unknown): Option<number> {
  if (typeof endpoint !== "object" || endpoint === null || !("dse" in endpoint)) return null;
  const dse = endpoint.dse;
  return typeof dse === "number" ? dse : null;
}

/** Midnight UTC on the given `NaiveDate`, or null when absent. */
function fullDayStart(date: Option<NaiveDate>): Option<DateTime<Utc>> {
  if (!date) return null;
  return new DateTime(date.withTime(), Utc);
}

/**
 * Midnight UTC at the end of a full-day event. The wire form
 * stores `endDate` as an **exclusive** bound (Google convention):
 * a 1-day event Apr 18 has `startDate=2026-04-18`,
 * `endDate=2026-04-19`. We preserve that semantic — the
 * synthesized `end` is midnight UTC on `endDate`, exclusive of
 * the last day's instant.
 */
function fullDayEnd(date: Option<NaiveDate>): Option<DateTime<Utc>> {
  if (!date) return null;
  return new DateTime(date.withTime(), Utc);
}
